import React, { useEffect, useMemo } from 'react';
import styles from 'components/medicalHistory/page.module.scss';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import Button from 'components/common/button';
import { Controller, useForm } from 'react-hook-form';
import OutlineButton from 'components/common/outlineButton';
import Wrapper from 'components/common/Wrapper';
import MetaTag from 'components/meta';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullablePatientDTO } from 'types/patients';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import { isEmptyString } from 'utils/helper';
import { SocialHistoryDTO } from 'artifacts/dtos';
import { CommonSelectOption } from 'components/medicalHistory/types/types';
import { SOCIAL_HISTORY_QUESTIONS } from 'components/medicalHistory/constants';

type OptionProps = {
  onChange?: (value?: string) => void;
  value?: string | null;
  error?: boolean;
  option: Readonly<CommonSelectOption[]>;
}

function Options({
  onChange, error, value, option,
}: OptionProps) {
  return (
    <div className="flex gap-4">
      {
        option?.map((option) => (
          <OutlineButton
            onClick={() => onChange?.(option.value)}
            isActive={option?.value === value}
            error={error}
            className="!normal-case !font-normal"
          >
            {option.label}
          </OutlineButton>
        ))
      }
    </div>
  );
}

const underAgeOptions = ['tobaccoUsedInLast12Months', 'alcoholConsumedInLast12Months'] as const;
const allOptions = [...underAgeOptions, 'haveYouEverPutInAnAdvanceCarePlan', 'hadOneOrMoreMinorOrFallsInLast12Months'] as const;

const checkFieldValidate = (
  value: string | null | undefined,
  fields: SocialHistoryDTO,
  isAboveAge: boolean,
) => {
  const checkKeys = isAboveAge ? allOptions : underAgeOptions;
  const hasNonEmptyField = checkKeys.every((key) => isEmptyString(fields[key]));
  if (hasNonEmptyField) return hasNonEmptyField;
  return !isEmptyString(value);
};

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function SocialHistoryScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const {
    control,
    reset,
    handleSubmit,
  } = useForm<SocialHistoryDTO>({
    mode: 'onChange',
  });
  const isAboveAge = useMemo(() => {
    const patientDOB = dayjs(patient?.dateOfBirth);
    return dayjs().diff(patientDOB, 'year') >= 65;
  }, [patient?.dateOfBirth]);

  const [state, dispatch] = usePersistMHReducer();

  const onSubmit = async (values: SocialHistoryDTO) => {
    await dispatch({
      type: actionPage,
      name: 'Social History',
      value: isAboveAge ? values : pick(values, underAgeOptions),
    });
    nextOrSubmitButton(nextPage);
  };

  // Back to state values
  useEffect(() => {
    if (state) {
      const socialHistory = state.find(
        (page: StateTypeLocalStorage) => page.name === actionPage,
      );
      if (Object.keys(socialHistory?.value).length) {
        reset(socialHistory?.value);
      } else {
        reset(patient?.socialHistory);
      }
    }
  }, [state]);

  const {
    tobaccoUsedInLast12Months,
    alcoholConsumedInLast12Months,
    haveYouEverPutInAnAdvanceCarePlan,
    hadOneOrMoreMinorOrFallsInLast12Months,
  } = SOCIAL_HISTORY_QUESTIONS;

  return (
    <>
      <MetaTag title="Social History" />
      <div className="pb-4 mb-4">
        <div>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Social History
          </h2>
          <Controller
            name="tobaccoUsedInLast12Months"
            control={control}
            rules={{ validate: (value, fields) => checkFieldValidate(value, fields, isAboveAge) }}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                id="tobaccoUsedInLast12Months"
                label={tobaccoUsedInLast12Months?.name}
              >
                <Options
                  {...field}
                  option={tobaccoUsedInLast12Months?.option ?? []}
                  error={!isNil(error)}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="alcoholConsumedInLast12Months"
            control={control}
            rules={{ validate: (value, fields) => checkFieldValidate(value, fields, isAboveAge) }}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                id="alcoholConsumedInLast12Months"
                label={alcoholConsumedInLast12Months?.name}
                className="mt-3"

              >
                <Options
                  {...field}
                  option={alcoholConsumedInLast12Months?.option ?? []}
                  error={!isNil(error)}
                />
              </Wrapper>
            )}
          />
          {isAboveAge && (
          <>
            <Controller
              name="haveYouEverPutInAnAdvanceCarePlan"
              control={control}
              rules={{ validate: (value, fields) => checkFieldValidate(value, fields, isAboveAge) }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="haveYouEverPutInAnAdvanceCarePlan"
                  label={haveYouEverPutInAnAdvanceCarePlan?.name}
                  className="mt-3"
                >
                  <Options
                    {...field}
                    option={haveYouEverPutInAnAdvanceCarePlan?.option ?? []}
                    error={!isNil(error)}
                  />
                </Wrapper>
              )}
            />
            <Controller
              name="hadOneOrMoreMinorOrFallsInLast12Months"
              control={control}
              rules={{ validate: (value, fields) => checkFieldValidate(value, fields, isAboveAge) }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="hadOneOrMoreMinorOrFallsInLast12Months"
                  label={hadOneOrMoreMinorOrFallsInLast12Months?.name}
                  className="mt-3 whitespace-pre-line"
                >
                  <Options
                    {...field}
                    option={hadOneOrMoreMinorOrFallsInLast12Months?.option ?? []}
                    error={!isNil(error)}
                  />
                </Wrapper>
              )}
            />
          </>
          )}
        </div>
        <div className="next-button-fixed">
          <Button onClick={handleSubmit(onSubmit)}>
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

export default SocialHistoryScreen;
